/**
 * Node visual para geração automática de cobrança no Flow Builder
 * Exibe configuração de cliente, itens, pagamento e tipo de cobrança
 * 
 * RELEASE: 1.0.0 - 2026-03-22 - Novo node charge_generate
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { DollarSign, Users, ShoppingCart, CreditCard, Calendar, FileText, Save, FileCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FlowNodeData, ChargeGenerateConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';
import { FeatureHelp } from '@/components/ui/feature-help';

/**
 * Componente visual do node Gerar Cobrança
 * Exibe cliente, itens, pagamento e tipo configurados
 */
export function ChargeGenerateNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as ChargeGenerateConfig | undefined;

  // Verifica se node está configurado
  const isConfigured = !!config?.conta_corrente_id;

  // Labels para exibição
  const customerLabel = config?.customer_selection === 'from_contact' ? 'Do contato'
    : config?.customer_selection === 'from_deal' ? 'Do negócio'
    : config?.customer_selection === 'from_variable' ? 'Variável'
    : '—';

  const itemsLabel = config?.items_source === 'from_deal' ? 'Itens do negócio'
    : config?.items_source === 'fixed' ? `${config.fixed_items?.length || 0} produto(s) fixo(s)`
    : '—';

  return (
    <>
      {/* Toolbar flutuante para edição/exclusão */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />

      {/* Handle de entrada (topo) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white"
      />

      {/* Corpo do node */}
      <div className={`
        min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
        ${selected ? 'border-emerald-500 ring-2 ring-emerald-500/30' : 'border-emerald-400'}
        transition-all duration-200
      `}>
        {/* Header */}
        <div className="flex items-center gap-2 p-3 border-b border-border bg-emerald-500/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-emerald-500">
            <DollarSign className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm flex-1">
            {config?.nickname || 'Gerar Cobrança'}
          </span>
          <FeatureHelp featureKey="chargeGenerate" size="sm" variant="subtle" />
        </div>

        {/* Conteúdo */}
        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              {/* Cliente */}
              <div className="flex items-center gap-1.5">
                <Users className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Cliente:</span>
                <span className="font-medium">{customerLabel}</span>
              </div>

              {/* Itens */}
              <div className="flex items-center gap-1.5">
                <ShoppingCart className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Itens:</span>
                <span className="font-medium">{itemsLabel}</span>
              </div>

              {/* Meio de pagamento */}
              {config.meio_pagamento_nome && (
                <div className="flex items-center gap-1.5">
                  <CreditCard className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Pagamento:</span>
                  <span className="font-medium truncate">{config.meio_pagamento_nome}</span>
                </div>
              )}

              {/* Tipo de cobrança */}
              <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                <FileText className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Tipo:</span>
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                  {config.tipo_cobranca === 'parcelada'
                    ? `Parcelada ${config.numero_parcelas}x`
                    : 'Única'}
                </Badge>
              </div>

              {/* Vencimento */}
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Vencimento:</span>
                <span className="font-medium">
                  {config.due_date_mode === 'days_after'
                    ? `+${config.due_date_days || 7} dias`
                    : 'Variável'}
                </span>
              </div>

              {/* CPF/CNPJ do cliente */}
              {config.cpf_cnpj_variable && (
                <div className="flex items-center gap-1.5">
                  <FileCheck className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Doc:</span>
                  <code className="bg-emerald-100 dark:bg-emerald-900/30 px-1 rounded text-[10px]">
                    {`{{${config.cpf_cnpj_variable}}}`}
                  </code>
                </div>
              )}

              {/* Indicador de saída: cobranca_id */}
              <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 pt-1 border-t border-border/50">
                <span>→ Salva em:</span>
                <code className="bg-emerald-100 dark:bg-emerald-900/30 px-1 rounded text-[10px]">
                  {'{{cobranca_id}}'}
                </code>
              </div>

              {/* Variável de resultado */}
              {config.result_variable && (
                <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                  <Save className="h-3 w-3 text-emerald-500" />
                  <span className="text-muted-foreground">Resultado:</span>
                  <code className="bg-emerald-100 dark:bg-emerald-900/30 px-1 rounded text-[10px]">
                    {`{{${config.result_variable}}}`}
                  </code>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground italic text-center py-2">
              Clique para configurar a cobrança
            </p>
          )}
        </div>
      </div>

      {/* Handle de saída (base) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white"
      />
    </>
  );
}
